import React, {
  createContext,
  useCallback,
  useContext,
  useReducer,
} from "react";

import DeliveryComment from "@/Models/DeliveryComment";
import api from "../Api";

export const COMMENT_MENU_TYPE = {
  GENERAL: "GENERAL",
  DELAYED: "DELAYED",
  CS: "CS",
  SENT_BACK: "SENT_BACK",
  LOST_DAMAGED: "LOST_DAMAGED",
};

export const COMMENT_FETCH_CONCURRENCY = 5;

export const CONSTANTS = {
  FETCH_BY_BOOK_ID: "FETCH_BY_BOOK_ID",
};

const INITIAL_STATE = {
  comments: [],
  bookId: "",
};

const reducer = (state, action) => {
  switch (action.type) {
    case CONSTANTS.FETCH_BY_BOOK_ID:
      return {
        ...state,
        bookId: action.bookId,
        comments: action.comments,
      };
    default:
      return INITIAL_STATE;
  }
};

export const normalizeCommentList = (response) => {
  if (Array.isArray(response)) {
    return response;
  }

  if (Array.isArray(response?.rows)) {
    return response.rows;
  }

  if (Array.isArray(response?.comments)) {
    return response.comments;
  }

  if (Array.isArray(response?.data)) {
    return response.data;
  }

  return [];
};

const mapWithConcurrency = async (items, limit, iteratee) => {
  const results = new Array(items.length);
  let cursor = 0;

  const workers = new Array(Math.min(limit, items.length))
    .fill(null)
    .map(async () => {
      while (cursor < items.length) {
        const index = cursor;
        cursor += 1;
        results[index] = await iteratee(items[index], index);
      }
    });

  await Promise.all(workers);
  return results;
};

export const fetchCommentsByBookId = async (bookId) => {
  if (!bookId) {
    return [];
  }

  try {
    const response = await api.get(
      `/deliveries/${encodeURIComponent(bookId)}/comments`,
    );

    return normalizeCommentList(response)
      .map((row) => new DeliveryComment(row))
      .sort((a, b) => {
        const aTime = a.createdAt ? new Date(a.createdAt).getTime() : 0;
        const bTime = b.createdAt ? new Date(b.createdAt).getTime() : 0;
        return aTime - bTime;
      });
  } catch (_e) {
    return [];
  }
};

export const fetchCommentsByBookIds = async (
  bookIds,
  concurrency = COMMENT_FETCH_CONCURRENCY,
) => {
  const uniqueBookIds = [...new Set((bookIds || []).filter(Boolean))];
  const commentsByBookId = {};

  await mapWithConcurrency(uniqueBookIds, concurrency, async (bookId) => {
    commentsByBookId[bookId] = await fetchCommentsByBookId(bookId);
  });

  return commentsByBookId;
};

export const deriveCommentConfirmed = (comments = []) => {
  if (!comments.length) {
    return null;
  }

  const unconfirmedFromAdmin = comments.some((comment) => comment.canConfirm());

  if (unconfirmedFromAdmin) {
    return false;
  }

  return true;
};

export const Context = createContext(INITIAL_STATE);

export const Provider = ({ children }) => {
  const [state, dispatch] = useReducer(reducer, INITIAL_STATE);

  return (
    <Context.Provider value={{ dispatch, state }}>{children}</Context.Provider>
  );
};

export const useDeliveryCommentsStore = () => {
  const { dispatch, state } = useContext(Context);

  const fetchByBookId = useCallback(
    async (bookId) => {
      const comments = await fetchCommentsByBookId(bookId);

      dispatch({
        type: CONSTANTS.FETCH_BY_BOOK_ID,
        bookId,
        comments,
      });

      return comments;
    },
    [dispatch],
  );

  const create = useCallback(async (bookId, { content, menuType }) => {
    return await api.post(`/deliveries/${encodeURIComponent(bookId)}/comments`, {
      content,
      menuType: menuType || COMMENT_MENU_TYPE.CS,
    });
  }, []);

  const update = useCallback(async (bookId, commentId, content) => {
    return await api.patch(
      `/deliveries/${encodeURIComponent(bookId)}/comments/${commentId}`,
      { content },
    );
  }, []);

  const remove = useCallback(async (bookId, commentId) => {
    return await api.delete(
      `/deliveries/${encodeURIComponent(bookId)}/comments/${commentId}`,
    );
  }, []);

  const confirm = useCallback(async (bookId, commentId) => {
    return await api.post(
      `/deliveries/${encodeURIComponent(bookId)}/comments/${commentId}/confirm`,
    );
  }, []);

  return {
    state,
    fetchByBookId,
    create,
    update,
    remove,
    confirm,
  };
};
